import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, Info, Pencil, Plus, Search, UserMinus, X } from "lucide-react";
import { clsx } from "clsx";
import apiClient from "@/api/client";
import { useAuthStore } from "@/store/auth";
import {
  useAddRoomMember,
  useRemoveRoomMember,
  useUpdateRoom,
} from "../api/chatApi";
import type { ChatRoomDetail, ChatUserBrief } from "../types";
import { Avatar } from "./Avatar";

interface InternalUserOption {
  id: string;
  username: string;
  first_name: string;
  last_name: string;
  role: string;
  branch: { id: string; name: string } | null;
  profile_picture_url: string | null;
  contractor_company_name?: string | null;
  // Wstęp konta podwykonawcy do GG. Konta BEZ tej flagi trafiają na listę tylko CEO —
  // to on ją nadaje, więc tylko on ma co z takim wierszem zrobić.
  has_chat_access?: boolean;
}

interface GroupMembersDialogProps {
  room: ChatRoomDetail;
  canManage: boolean;
  onClose: () => void;
}

export function GroupMembersDialog({
  room,
  canManage,
  onClose,
}: GroupMembersDialogProps) {
  const me = useAuthStore((s) => s.user);
  const addMember = useAddRoomMember(room.id);
  const removeMember = useRemoveRoomMember(room.id);
  const updateRoom = useUpdateRoom();
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState("");
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(room.name);

  async function saveName() {
    const next = nameDraft.trim();
    if (!next || next === room.name) {
      setEditingName(false);
      return;
    }
    await updateRoom.mutateAsync({ roomId: room.id, name: next });
    setEditingName(false);
  }

  const allUsers = useQuery({
    queryKey: ["chat", "selectable-users"],
    enabled: adding,
    queryFn: async () => {
      // `selectable=chat`, nie `true`: ten wariant dokłada konta podwykonawców
      // wpuszczone do GG przez CEO. Zwykłe `true` zostaje wyłącznie dla GK.
      const r = await apiClient.get<{ data: InternalUserOption[] }>(
        "/auth/users/?selectable=chat",
      );
      return r.data.data ?? [];
    },
  });

  const existingIds = useMemo(
    () => new Set(room.members.map((m) => m.id)),
    [room.members],
  );

  /** Dodanie do grupy. Firma bez wstępu do GG (widzi ją tylko CEO) dostaje ten wstęp
   *  dopiero po jawnym potwierdzeniu — backend bez `grantChatAccess` i tak odmówi. */
  function pick(u: InternalUserOption) {
    const needsGrant = u.role === "contractor" && !u.has_chat_access;
    if (needsGrant) {
      const who = `${u.first_name} ${u.last_name}`.trim() || u.username;
      const company = u.contractor_company_name ? ` (${u.contractor_company_name})` : "";
      if (
        !window.confirm(
          `${who}${company} nie ma jeszcze wstępu do komunikatora GG.\n\n` +
            "Dodanie do grupy nada temu kontu dostęp do GG. Rozmowę zobaczy dopiero " +
            "od tej chwili, ale od tej chwili zobaczy wszystko, co w niej napiszecie.\n\n" +
            "Wpuścić?",
        )
      )
        return;
    }
    addMember.mutate({ userId: u.id, grantChatAccess: needsGrant });
  }

  const candidates = useMemo(() => {
    const list = allUsers.data ?? [];
    const term = search.trim().toLowerCase();
    return list
      .filter((u) => !existingIds.has(u.id))
      .filter((u) => {
        if (!term) return true;
        return (
          u.username.toLowerCase().includes(term) ||
          `${u.first_name} ${u.last_name}`.toLowerCase().includes(term) ||
          (u.branch?.name ?? "").toLowerCase().includes(term) ||
          (u.contractor_company_name ?? "").toLowerCase().includes(term)
        );
      })
      .sort((a, b) => {
        // Pracownicy GK najpierw — podwykonawca to wyjątek na tej liście, nie
        // pozycja, na którą wpada się przypadkiem szukając kolegi z biura.
        const ac = a.role === "contractor" ? 1 : 0;
        const bc = b.role === "contractor" ? 1 : 0;
        if (ac !== bc) return ac - bc;
        const an = `${a.first_name} ${a.last_name}`.trim() || a.username;
        const bn = `${b.first_name} ${b.last_name}`.trim() || b.username;
        return an.localeCompare(bn, "pl");
      });
  }, [allUsers.data, existingIds, search]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white border border-gray-200 rounded-lg w-full max-w-md shadow-2xl flex flex-col max-h-[85vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-sm font-mono font-semibold text-gray-900">
            Członkowie rozmowy
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded text-gray-500"
          >
            <X size={16} />
          </button>
        </div>

        {!adding ? (
          <>
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center gap-2">
              {editingName ? (
                <>
                  <input
                    autoFocus
                    type="text"
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") void saveName();
                      if (e.key === "Escape") {
                        setEditingName(false);
                        setNameDraft(room.name);
                      }
                    }}
                    className="flex-1 rounded-md border border-gray-300 bg-white text-sm px-2 py-1 text-gray-900 focus:outline-none focus:border-red-400"
                  />
                  <button
                    type="button"
                    onClick={() => void saveName()}
                    disabled={updateRoom.isPending}
                    title="Zapisz"
                    className="p-1 rounded text-red-600 hover:bg-red-50 disabled:opacity-50"
                  >
                    <Check size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingName(false);
                      setNameDraft(room.name);
                    }}
                    title="Anuluj"
                    className="p-1 rounded text-gray-500 hover:bg-gray-100"
                  >
                    <X size={16} />
                  </button>
                </>
              ) : (
                <>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-gray-500">
                      Nazwa grupy
                    </div>
                    <div className="text-sm font-semibold text-gray-900 truncate">
                      {room.name || "(bez nazwy)"}
                    </div>
                  </div>
                  {canManage && (
                    <button
                      type="button"
                      onClick={() => {
                        setNameDraft(room.name);
                        setEditingName(true);
                      }}
                      title="Edytuj nazwę"
                      className="p-1 rounded text-gray-600 hover:bg-gray-100"
                    >
                      <Pencil size={14} />
                    </button>
                  )}
                </>
              )}
            </div>
            <div className="flex-1 overflow-y-auto">
              {room.members.map((m) => (
                <MemberRow
                  key={m.id}
                  member={m}
                  isCreator={m.id === room.created_by}
                  isSelf={m.id === me?.id}
                  canManage={canManage}
                  onRemove={() => {
                    if (
                      !confirm(
                        `Usunąć ${m.full_name || m.username} z rozmowy?`,
                      )
                    )
                      return;
                    removeMember.mutate(m.id);
                  }}
                />
              ))}
            </div>
            {canManage && (
              <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
                <button
                  type="button"
                  onClick={() => setAdding(true)}
                  className="w-full rounded-md bg-red-600 text-white px-3 py-2 text-sm font-medium hover:bg-red-700 flex items-center justify-center gap-1"
                >
                  <Plus size={14} /> Dodaj uczestnika
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Kto dodaje, ten ma wiedzieć, co dodawany zobaczy — a zobaczy
                rozmowę dopiero od tej chwili. Reguła jest w apps/chat/visibility.py
                i nie ma od niej wyjątku ani dla podwykonawcy, ani dla GK. */}
            <div className="px-4 pt-3">
              <div className="flex gap-2 border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] leading-snug text-amber-900">
                <Info size={14} className="mt-px shrink-0 text-amber-600" />
                <span>
                  Osoba, którą dodasz, zobaczy rozmowę <strong>dopiero od tego
                  momentu</strong>. Dotychczasowa historia pozostanie dla niej
                  niewidoczna — tak samo dla pracownika GK, jak i dla podwykonawcy.
                </span>
              </div>
            </div>
            <div className="px-4 pt-3">
              <div className="relative">
                <Search
                  size={14}
                  className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  autoFocus
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Szukaj po imieniu, nazwisku, oddziale lub firmie…"
                  className="w-full rounded-md border border-gray-300 bg-white text-sm pl-7 pr-3 py-2 text-gray-900 focus:outline-none focus:border-red-400"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-2">
              <div className="border border-gray-200 rounded-md overflow-hidden">
                {allUsers.isLoading && (
                  <div className="p-3 text-xs text-gray-500">Wczytuję…</div>
                )}
                {candidates.map((u) => {
                  const full =
                    `${u.first_name} ${u.last_name}`.trim() || u.username;
                  // Firma bez wstępu do GG — na czerwono, bo to nie jest zwykłe dodanie
                  // do rozmowy, tylko wpuszczenie kogoś spoza GK do komunikatora.
                  const needsGrant = u.role === "contractor" && !u.has_chat_access;
                  return (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => pick(u)}
                      disabled={addMember.isPending}
                      className={clsx(
                        "w-full text-left flex items-center gap-2 px-3 py-2 text-sm border-b border-gray-100 last:border-b-0 disabled:opacity-50",
                        needsGrant ? "bg-red-50/60 hover:bg-red-100" : "hover:bg-red-50",
                      )}
                    >
                      {u.profile_picture_url ? (
                        <img
                          src={u.profile_picture_url}
                          alt={full}
                          className="w-7 h-7 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-amber-100 text-amber-700 font-mono text-[11px] font-semibold flex items-center justify-center">
                          {(
                            (u.first_name?.[0] ?? "") +
                            (u.last_name?.[0] ?? "")
                          ).toUpperCase() || u.username[0].toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-gray-900 font-medium truncate">
                          {full}
                        </div>
                        {u.role === "contractor" ? (
                          <div
                            className={clsx(
                              "text-[11px] truncate",
                              needsGrant ? "text-red-700 font-semibold" : "text-amber-700",
                            )}
                          >
                            Podwykonawca
                            {u.contractor_company_name
                              ? ` · ${u.contractor_company_name}`
                              : ""}
                            {needsGrant && " · BEZ WSTĘPU DO GG"}
                          </div>
                        ) : (
                          u.branch?.name && (
                            <div className="text-[11px] text-gray-500 truncate">
                              {u.branch.name}
                            </div>
                          )
                        )}
                      </div>
                      <Plus size={14} className="text-red-600" />
                    </button>
                  );
                })}
                {!allUsers.isLoading && candidates.length === 0 && (
                  <div className="p-3 text-xs text-gray-500">
                    Brak pasujących użytkowników.
                  </div>
                )}
              </div>
            </div>
            <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2 bg-gray-50">
              <button
                type="button"
                onClick={() => {
                  setAdding(false);
                  setSearch("");
                }}
                className="px-3 py-1.5 text-sm rounded-md hover:bg-gray-100 text-gray-700"
              >
                Gotowe
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function MemberRow({
  member,
  isSelf,
  isCreator,
  canManage,
  onRemove,
}: {
  member: ChatUserBrief;
  isSelf: boolean;
  isCreator: boolean;
  canManage: boolean;
  onRemove: () => void;
}) {
  const name = member.full_name || member.username;
  return (
    <div
      className={clsx(
        "flex items-center gap-3 px-3 py-2 border-b border-gray-100 last:border-b-0",
      )}
    >
      <Avatar user={member} size={32} />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-gray-900 truncate">
          {isSelf ? `${name} (Ty)` : name}
          {isCreator && (
            <span className="ml-2 text-[10px] text-gray-500 uppercase tracking-wider">
              założyciel
            </span>
          )}
          {member.is_external && (
            <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-amber-800">
              spoza GK
            </span>
          )}
        </div>
        {member.is_external ? (
          <div className="text-[11px] text-amber-700 truncate">
            {member.company_name || "podwykonawca"}
          </div>
        ) : (
          member.username && (
            <div className="text-[11px] text-gray-500 truncate">
              {member.username}
            </div>
          )
        )}
      </div>
      {canManage && !isCreator && (
        <button
          type="button"
          onClick={onRemove}
          title="Usuń z rozmowy"
          className="p-1 rounded hover:bg-red-50 text-red-600"
        >
          <UserMinus size={14} />
        </button>
      )}
    </div>
  );
}
